// src/pages/admin/AdminLogisticsPage.jsx
// Phase 5 — M5: Admin logistics command centre

import { useEffect, useState } from 'react'

const PINK  = '#FF3D7F'
const ORG   = '#FF8500'
const DARK  = '#1A1A2E'
const GREY  = '#9CA3AF'
const GR_LT = '#F3F4F6'

const api = (path, opts = {}) =>
  fetch(`/api/v1${path}`, {
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
    ...opts,
  }).then(r => r.json())

const VERDICT_STYLES = {
  pass:        { bg: '#DCFCE7', color: '#166534', icon: '✅' },
  fail:        { bg: '#FEE2E2', color: '#991B1B', icon: '❌' },
  conditional: { bg: '#FEF3C7', color: '#92400E', icon: '⚠️' },
}

const SHIPMENT_COLORS = { delivered:'#10B981', out_for_delivery:'#3B82F6', in_transit:'#6366F1', picked_up:'#8B5CF6', booked:'#F59E0B', pending:'#9CA3AF', failed:'#EF4444', rto_initiated:'#EF4444', rto_delivered:'#DC2626' }

function KpiCard({ label, value, sub, color = PINK }) {
  return (
    <div style={{ background: '#fff', borderRadius: 16, padding: '20px 24px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', borderTop: `3px solid ${color}` }}>
      <div style={{ fontSize: '1.7rem', fontWeight: 800, color }}>{value}</div>
      <div style={{ fontWeight: 600, color: DARK, fontSize: '0.88rem', marginTop: 2 }}>{label}</div>
      {sub && <div style={{ fontSize: '0.75rem', color: GREY, marginTop: 2 }}>{sub}</div>}
    </div>
  )
}

export default function AdminLogisticsPage() {
  const [overview, setOverview]     = useState(null)
  const [qcReqs, setQcReqs]         = useState([])
  const [shipments, setShipments]   = useState([])
  const [loading, setLoading]       = useState(true)
  const [tab, setTab]               = useState('overview')
  const [assignModal, setAssignModal] = useState(null)  // requestId
  const [reportModal, setReportModal] = useState(null)  // requestId
  const [assignForm, setAssignForm]   = useState({ inspector_id: '', inspection_date: '' })
  const [reportForm, setReportForm]   = useState({ verdict: 'pass', summary: '', defect_rate_pct: '', corrective_action: '', test_results: '[{"name":"Visual","result":"OK","pass":true}]' })
  const [saving, setSaving]           = useState(false)
  const [shipFilter, setShipFilter]   = useState('')
  const [qcFilter, setQcFilter]       = useState('')

  const load = () => {
    setLoading(true)
    Promise.all([
      api('/admin/logistics/overview'),
      api(`/admin/logistics/qc-requests${qcFilter ? `?status=${qcFilter}` : ''}`),
      api(`/admin/logistics/shipments${shipFilter ? `?status=${shipFilter}` : ''}`),
    ]).then(([oRes, qRes, sRes]) => {
      setOverview(oRes.data ?? oRes)
      setQcReqs(qRes.data?.data ?? qRes.data ?? [])
      setShipments(sRes.data?.data ?? sRes.data ?? [])
      setLoading(false)
    })
  }

  useEffect(() => { load() }, [qcFilter, shipFilter])

  const assign = async () => {
    setSaving(true)
    await api(`/admin/logistics/qc-requests/${assignModal}/assign`, { method: 'POST', body: JSON.stringify(assignForm) })
    setSaving(false); setAssignModal(null); setAssignForm({ inspector_id: '', inspection_date: '' }); load()
  }

  const submitReport = async () => {
    setSaving(true)
    let testResults = []
    try { testResults = JSON.parse(reportForm.test_results) } catch {}
    await api(`/admin/logistics/qc-requests/${reportModal}/report`, {
      method: 'POST',
      body: JSON.stringify({ verdict: reportForm.verdict, summary: reportForm.summary, corrective_action: reportForm.corrective_action, test_results: testResults })
    })
    setSaving(false); setReportModal(null); setReportForm({ verdict: 'pass', summary: '', defect_rate_pct: '', corrective_action: '', test_results: '[{"name":"Visual","result":"OK","pass":true}]' }); load()
  }

  const syncTracking = async (id) => {
    await api(`/admin/logistics/shipments/${id}/sync`, { method: 'POST' })
    load()
  }

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: GREY }}>Loading logistics data…</div>

  const ship    = overview?.shipping  ?? {}
  const wh      = overview?.warehouse ?? {}
  const quality = overview?.quality   ?? {}

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 0 56px' }}>
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontFamily: 'Playfair Display, serif', fontWeight: 800, fontSize: '1.85rem', color: DARK, margin: 0 }}>Logistics Command Centre</h1>
        <p style={{ color: GREY, margin: '4px 0 0', fontSize: '0.88rem' }}>Shipment tracking, quality control, and warehouse oversight</p>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, background: GR_LT, borderRadius: 12, padding: 4, width: 'fit-content', marginBottom: 24 }}>
        {[['overview','Overview'],['qc','Quality Control'],['shipments','Shipments']].map(([k,l]) => (
          <button key={k} onClick={() => setTab(k)} style={{ padding: '8px 18px', borderRadius: 10, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: '0.83rem', background: tab===k ? '#fff' : 'transparent', color: tab===k ? PINK : '#6B7280', boxShadow: tab===k ? '0 1px 4px rgba(0,0,0,0.08)' : 'none' }}>{l}</button>
        ))}
      </div>

      {/* ── Overview ── */}
      {tab === 'overview' && (
        <div>
          <div style={{ marginBottom: 12, fontWeight: 700, color: DARK, fontSize: '0.9rem' }}>Shipments</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14, marginBottom: 28 }}>
            <KpiCard label="Active Shipments"    value={ship.active_shipments ?? 0}  color={PINK} />
            <KpiCard label="Delivered Today"      value={ship.delivered_today   ?? 0}  color="#10B981" />
            <KpiCard label="RTO / Returns"        value={ship.rto               ?? 0}  color="#EF4444" />
          </div>

          <div style={{ marginBottom: 12, fontWeight: 700, color: DARK, fontSize: '0.9rem' }}>Warehouse</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14, marginBottom: 28 }}>
            <KpiCard label="Active Warehouses"    value={wh.total_warehouses    ?? 0}  color={ORG} />
            <KpiCard label="Low Stock Items"      value={wh.low_stock_items     ?? 0}  color={wh.low_stock_items > 0 ? '#EF4444' : '#10B981'} />
            <KpiCard label="Zero Stock Items"     value={wh.zero_stock_items    ?? 0}  color="#EF4444" />
            <KpiCard label="Suppliers w/ WH"      value={wh.suppliers_with_wh   ?? 0}  color={DARK} />
          </div>

          <div style={{ marginBottom: 12, fontWeight: 700, color: DARK, fontSize: '0.9rem' }}>Quality Control</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 14 }}>
            <KpiCard label="Pending Assignment"   value={quality.pending_assignment ?? 0}  color={ORG} />
            <KpiCard label="In Progress"          value={quality.in_progress        ?? 0}  color="#8B5CF6" />
            <KpiCard label="Passed This Month"    value={quality.passed_month       ?? 0}  color="#10B981" />
            <KpiCard label="Failed This Month"    value={quality.failed_month       ?? 0}  color="#EF4444" />
            <KpiCard label="Avg Defect Rate"      value={`${quality.avg_defect_rate ?? 0}%`} color={DARK} />
          </div>
        </div>
      )}

      {/* ── Quality Control ── */}
      {tab === 'qc' && (
        <div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
            {['','pending','assigned','in_progress','completed'].map(s => (
              <button key={s} onClick={() => setQcFilter(s)} style={{ padding: '6px 14px', borderRadius: 10, border: `1.5px solid ${qcFilter===s ? PINK : '#E5E7EB'}`, background: qcFilter===s ? PINK : '#fff', color: qcFilter===s ? '#fff' : '#6B7280', fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer' }}>
                {s === '' ? 'All' : s.replace('_',' ')}
              </button>
            ))}
          </div>
          <div style={{ background: '#fff', borderRadius: 16, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: GR_LT }}>
                  {['Request No.','Supplier','Product','Qty','Type','Status','Verdict','Actions'].map(h => (
                    <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: '0.75rem', fontWeight: 700, color: GREY, textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {qcReqs.length === 0 && <tr><td colSpan={8} style={{ padding: 32, textAlign: 'center', color: GREY }}>No QC requests found.</td></tr>}
                {qcReqs.map((req, i) => {
                  const verdict = req.report?.verdict
                  const vs = verdict ? VERDICT_STYLES[verdict] : null
                  return (
                    <tr key={req.id} style={{ borderBottom: '1px solid #F9FAFB', background: i % 2 === 0 ? '#fff' : '#FAFAFA' }}>
                      <td style={{ padding: '12px 14px', fontWeight: 700, color: DARK, fontSize: '0.85rem', whiteSpace: 'nowrap' }}>{req.request_number}</td>
                      <td style={{ padding: '12px 14px', fontSize: '0.84rem', color: '#374151' }}>{req.supplier?.business_name ?? req.supplier?.name ?? `#${req.supplier_id}`}</td>
                      <td style={{ padding: '12px 14px', fontSize: '0.84rem', color: '#374151' }}>{req.product?.name ?? `#${req.product_id}`}</td>
                      <td style={{ padding: '12px 14px', fontSize: '0.84rem', color: DARK, fontWeight: 600 }}>{req.batch_quantity}m</td>
                      <td style={{ padding: '12px 14px', fontSize: '0.82rem', color: GREY }}>{req.type?.replace('_',' ')}</td>
                      <td style={{ padding: '12px 14px' }}>
                        <span style={{ background: req.status==='pending'?'#FEF3C7':req.status==='completed'?'#DCFCE7':'#EDE9FE', color: req.status==='pending'?'#92400E':req.status==='completed'?'#166534':'#5B21B6', padding:'2px 10px', borderRadius:20, fontSize:'0.72rem', fontWeight:700 }}>
                          {req.status}
                        </span>
                      </td>
                      <td style={{ padding: '12px 14px' }}>
                        {vs && <span style={{ background: vs.bg, color: vs.color, padding:'2px 8px', borderRadius:20, fontSize:'0.72rem', fontWeight:800 }}>{vs.icon} {verdict?.toUpperCase()}</span>}
                      </td>
                      <td style={{ padding: '12px 14px' }}>
                        <div style={{ display: 'flex', gap: 6 }}>
                          {req.status === 'pending' && (
                            <button onClick={() => setAssignModal(req.id)} style={{ padding: '4px 10px', borderRadius: 7, border: 'none', background: PINK, color: '#fff', fontWeight: 600, fontSize: '0.75rem', cursor: 'pointer' }}>Assign</button>
                          )}
                          {['assigned','in_progress'].includes(req.status) && (
                            <button onClick={() => setReportModal(req.id)} style={{ padding: '4px 10px', borderRadius: 7, border: 'none', background: ORG, color: '#fff', fontWeight: 600, fontSize: '0.75rem', cursor: 'pointer' }}>Report</button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Shipments ── */}
      {tab === 'shipments' && (
        <div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
            {['','pending','booked','in_transit','out_for_delivery','delivered','failed','rto_initiated'].map(s => (
              <button key={s} onClick={() => setShipFilter(s)} style={{ padding: '6px 14px', borderRadius: 10, border: `1.5px solid ${shipFilter===s ? PINK : '#E5E7EB'}`, background: shipFilter===s ? PINK : '#fff', color: shipFilter===s ? '#fff' : '#6B7280', fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer' }}>
                {s === '' ? 'All' : s.replace(/_/g,' ')}
              </button>
            ))}
          </div>
          <div style={{ background: '#fff', borderRadius: 16, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: GR_LT }}>
                  {['Order No.','AWB','Provider','Courier','Status','Est. Delivery','Actions'].map(h => (
                    <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: '0.75rem', fontWeight: 700, color: GREY, textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {shipments.length === 0 && <tr><td colSpan={7} style={{ padding: 32, textAlign: 'center', color: GREY }}>No shipments found.</td></tr>}
                {shipments.map((s, i) => {
                  const statusColor = SHIPMENT_COLORS[s.status] ?? GREY
                  return (
                    <tr key={s.id} style={{ borderBottom: '1px solid #F9FAFB', background: i % 2 === 0 ? '#fff' : '#FAFAFA' }}>
                      <td style={{ padding: '12px 14px', fontWeight: 700, color: DARK, fontSize: '0.85rem' }}>{s.order?.order_number ?? `#${s.order_id}`}</td>
                      <td style={{ padding: '12px 14px', fontFamily: 'monospace', fontSize: '0.82rem', color: '#374151' }}>{s.awb_number ?? '—'}</td>
                      <td style={{ padding: '12px 14px', fontSize: '0.82rem', color: GREY }}>{s.provider}</td>
                      <td style={{ padding: '12px 14px', fontSize: '0.84rem', color: '#374151' }}>{s.courier_name ?? '—'}</td>
                      <td style={{ padding: '12px 14px' }}>
                        <span style={{ background: statusColor+'22', color: statusColor, padding:'2px 10px', borderRadius:20, fontSize:'0.72rem', fontWeight:700 }}>
                          {s.status?.replace(/_/g,' ')}
                        </span>
                      </td>
                      <td style={{ padding: '12px 14px', fontSize: '0.82rem', color: GREY }}>
                        {s.estimated_delivery ? new Date(s.estimated_delivery).toLocaleDateString('en-IN', { day:'numeric', month:'short' }) : '—'}
                      </td>
                      <td style={{ padding: '12px 14px' }}>
                        <div style={{ display: 'flex', gap: 6 }}>
                          {s.awb_number && s.provider !== 'manual' && (
                            <button onClick={() => syncTracking(s.id)} style={{ padding: '4px 10px', borderRadius: 7, border: `1.5px solid ${PINK}`, background: '#fff', color: PINK, fontWeight: 600, fontSize: '0.75rem', cursor: 'pointer' }}>Sync</button>
                          )}
                          {s.tracking_url && (
                            <a href={s.tracking_url} target="_blank" rel="noreferrer" style={{ padding: '4px 10px', borderRadius: 7, border: '1.5px solid #E5E7EB', background: '#fff', color: '#374151', fontWeight: 600, fontSize: '0.75rem', textDecoration: 'none' }}>Track</a>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Assign Inspector Modal ── */}
      {assignModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 20, padding: 32, width: 400, maxWidth: '95vw', boxShadow: '0 20px 60px rgba(0,0,0,0.18)' }}>
            <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.2rem', fontWeight: 800, color: DARK, margin: '0 0 20px' }}>Assign Inspector</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#6B7280', marginBottom: 4 }}>Inspector User ID *</label>
                <input type="number" value={assignForm.inspector_id} onChange={e => setAssignForm(f => ({...f,inspector_id:e.target.value}))} style={{ width: '100%', padding: '9px 12px', borderRadius: 9, border: '1.5px solid #E5E7EB', fontSize: '0.87rem', boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#6B7280', marginBottom: 4 }}>Inspection Date</label>
                <input type="date" value={assignForm.inspection_date} onChange={e => setAssignForm(f => ({...f,inspection_date:e.target.value}))} style={{ width: '100%', padding: '9px 12px', borderRadius: 9, border: '1.5px solid #E5E7EB', fontSize: '0.87rem', boxSizing: 'border-box' }} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
              <button onClick={() => setAssignModal(null)} style={{ flex: 1, padding: '10px', borderRadius: 10, border: '1.5px solid #E5E7EB', background: '#fff', color: '#6B7280', fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
              <button onClick={assign} disabled={saving} style={{ flex: 2, padding: '10px', borderRadius: 10, border: 'none', background: PINK, color: '#fff', fontWeight: 700, cursor: 'pointer' }}>{saving ? 'Assigning…' : 'Assign Inspector'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Submit QC Report Modal ── */}
      {reportModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 20, padding: 32, width: 480, maxWidth: '95vw', boxShadow: '0 20px 60px rgba(0,0,0,0.18)', maxHeight: '90vh', overflowY: 'auto' }}>
            <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.2rem', fontWeight: 800, color: DARK, margin: '0 0 20px' }}>Submit QC Report</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#6B7280', marginBottom: 4 }}>Verdict *</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  {['pass','fail','conditional'].map(v => (
                    <button key={v} onClick={() => setReportForm(f => ({...f,verdict:v}))} style={{ flex: 1, padding: '9px', borderRadius: 9, border: `2px solid ${reportForm.verdict===v ? (v==='pass'?'#10B981':v==='fail'?'#EF4444':ORG) : '#E5E7EB'}`, background: reportForm.verdict===v ? (v==='pass'?'#DCFCE7':v==='fail'?'#FEE2E2':'#FEF3C7') : '#fff', color: v==='pass'?'#166534':v==='fail'?'#991B1B':'#92400E', fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer', textTransform: 'uppercase' }}>
                      {VERDICT_STYLES[v]?.icon} {v}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#6B7280', marginBottom: 4 }}>Summary *</label>
                <textarea rows={3} value={reportForm.summary} onChange={e => setReportForm(f => ({...f,summary:e.target.value}))} style={{ width: '100%', padding: '9px 12px', borderRadius: 9, border: '1.5px solid #E5E7EB', fontSize: '0.87rem', resize: 'vertical', boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#6B7280', marginBottom: 4 }}>Corrective Action (if fail/conditional)</label>
                <textarea rows={2} value={reportForm.corrective_action} onChange={e => setReportForm(f => ({...f,corrective_action:e.target.value}))} style={{ width: '100%', padding: '9px 12px', borderRadius: 9, border: '1.5px solid #E5E7EB', fontSize: '0.87rem', resize: 'vertical', boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#6B7280', marginBottom: 4 }}>Test Results (JSON array)</label>
                <textarea rows={3} value={reportForm.test_results} onChange={e => setReportForm(f => ({...f,test_results:e.target.value}))} style={{ width: '100%', padding: '9px 12px', borderRadius: 9, border: '1.5px solid #E5E7EB', fontSize: '0.82rem', fontFamily: 'monospace', resize: 'vertical', boxSizing: 'border-box' }} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
              <button onClick={() => setReportModal(null)} style={{ flex: 1, padding: '10px', borderRadius: 10, border: '1.5px solid #E5E7EB', background: '#fff', color: '#6B7280', fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
              <button onClick={submitReport} disabled={saving} style={{ flex: 2, padding: '10px', borderRadius: 10, border: 'none', background: ORG, color: '#fff', fontWeight: 700, cursor: 'pointer' }}>{saving ? 'Submitting…' : 'Submit Report'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
